import React, { useRef, useState } from "react";
import { HiX, HiCheck } from "react-icons/hi";

interface AddNoteLogFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (notes: string, logDate?: string) => Promise<void>;
  dateID: string;
  editMode?: boolean;
  initialNotes?: string;
  logId?: string;
}

const AddNoteLogForm: React.FC<AddNoteLogFormProps> = ({
  open,
  onClose,
  onSubmit,
  dateID,
  editMode = false,
  initialNotes = "",
  logId,
}) => {
  const [notes, setNotes] = useState(initialNotes);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Convert between a YYYYMMDD dateID and a YYYY-MM-DD <input type="date"> value
  const dateIDToInputValue = (id: string): string => {
    if (!id || id.length < 8) return "";
    return `${id.substring(0, 4)}-${id.substring(4, 6)}-${id.substring(6, 8)}`;
  };
  const inputValueToDateID = (val: string): string => val.replace(/-/g, "");

  const [dateInput, setDateInput] = useState<string>(
    dateIDToInputValue(dateID),
  );
  const dateInputRef = useRef<HTMLInputElement>(null);

  const openDatePicker = () => {
    const el = dateInputRef.current as
      | (HTMLInputElement & { showPicker?: () => void })
      | null;
    if (!el) return;
    try {
      if (typeof el.showPicker === "function") {
        el.showPicker();
      } else {
        el.focus();
      }
    } catch {
      el.focus();
    }
  };

  const formatDate = (dateID: string) => {
    const year = parseInt(dateID.substring(0, 4));
    const month = parseInt(dateID.substring(4, 6));
    const day = parseInt(dateID.substring(6, 8));
    const date = new Date(year, month - 1, day);

    return date.toLocaleDateString("en-US", {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  const handleSubmit = async () => {
    if (!notes.trim()) return;

    setIsSubmitting(true);
    try {
      await onSubmit(notes.trim(), inputValueToDateID(dateInput) || dateID);
      setNotes("");
      onClose();
    } catch (error) {
      console.error("Error adding note log:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setNotes(editMode ? initialNotes : "");
    onClose();
  };

  // Update notes when initial notes change (for edit mode)
  React.useEffect(() => {
    if (editMode && initialNotes !== undefined) {
      setNotes(initialNotes);
    } else if (!editMode) {
      setNotes("");
    }
    setDateInput(dateIDToInputValue(dateID));
  }, [editMode, initialNotes, open, dateID]);

  if (!open) return null;

  const displayDateID = inputValueToDateID(dateInput) || dateID;

  return (
    <div
      className="fixed inset-0 z-[9999] bg-black/40 backdrop-blur-sm overflow-y-auto"
      style={{ transform: "translateZ(0)" }}
    >
      <div className="flex min-h-screen md:items-center md:justify-center items-start justify-center">
        <div className="w-full max-w-2xl mx-auto overflow-hidden rounded-none md:rounded-md bg-base-100 text-left align-middle shadow-xl min-h-screen md:min-h-0 md:max-h-[90vh] flex flex-col">
          {/* Header */}
          <div className="flex justify-between items-start p-6 flex-shrink-0">
            <div>
              <h2 className="text-lg font-grotesk">
                {editMode ? "Edit Note" : "Add Note"}
              </h2>
              {/* Clickable date — opens a native date picker */}
              <div className="relative mt-1">
                <button
                  type="button"
                  onClick={openDatePicker}
                  className="flex items-center gap-1.5 text-sm text-base-content/60 hover:text-base-content transition-colors"
                >
                  <span className="font-medium underline decoration-dotted underline-offset-4">
                    {formatDate(displayDateID)}
                  </span>
                </button>
                <input
                  ref={dateInputRef}
                  type="date"
                  value={dateInput}
                  onChange={(e) => setDateInput(e.target.value)}
                  className="absolute left-0 top-6 w-0 h-0 opacity-0 pointer-events-none"
                  tabIndex={-1}
                  aria-hidden="true"
                />
              </div>
            </div>
            <button
              onClick={handleClose}
              className="bg-base-200 p-2 rounded-full"
              style={{ touchAction: "manipulation" }}
            >
              <HiX className="w-5 h-5 text-base-content/80" />
            </button>
          </div>

          {/* Content - Scrollable */}
          <div
            className="flex-1 overflow-y-auto md:p-6 space-y-6"
            style={{
              WebkitOverflowScrolling: "touch",
              touchAction: "pan-y",
              transform: "translateZ(0)",
              maxHeight: "calc(100vh - 200px)",
              minHeight: "0",
            }}
          >
            <div className="form-control h-full">
              <textarea
                className="textarea text-[16px] sm:text-sm h-full md:h-32 bg-base-200 resize-none focus:outline-none focus:ring-0 focus:border-transparent"
                style={{ lineHeight: "1.3" }}
                placeholder="Add your notes here..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                autoFocus
              />
            </div>
          </div>

          {/* Footer — floats over the scrolling content with a base→transparent fade */}
          <div className="px-5 pb-5 pt-28 -mt-24 flex-shrink-0 relative z-50 flex items-center justify-end gap-3 bg-gradient-to-t from-base-100 from-30% to-transparent pointer-events-none">
            <button
              onClick={() => {
                if (document.activeElement instanceof HTMLElement) {
                  document.activeElement.blur();
                }
                setTimeout(() => {
                  handleSubmit();
                }, 50);
              }}
              disabled={!notes.trim() || isSubmitting}
              aria-label={editMode ? "Update note" : "Add note"}
              title={editMode ? "Update note" : "Add note"}
              className="pointer-events-auto w-14 h-14 rounded-full bg-primary text-white flex items-center justify-center shadow-lg transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ touchAction: "manipulation" }}
            >
              {isSubmitting ? (
                <span className="loading loading-spinner loading-sm" />
              ) : (
                <HiCheck className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddNoteLogForm;
